import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';
import Avatar from '../../../components/ui/Avatar';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';

// Arrow flashes — tap the matching direction button as fast as possible. 15 rounds.
// Score = Math.max(1, 1500 − avgMs) for correct taps. Higher = faster = better.
const DIRS = [
  { id: 'left',  sym: '←', label: 'Links', labelEN: 'Left'  },
  { id: 'up',    sym: '↑', label: 'Omhoog',labelEN: 'Up'    },
  { id: 'right', sym: '→', label: 'Rechts',labelEN: 'Right' },
  { id: 'down',  sym: '↓', label: 'Omlaag',labelEN: 'Down'  },
];

function DirectionFlashGame({ lang, onBack, allUsers, friends, currentUserId, userName, invitePartner }) {
  const nl = lang === 'nl';
  const ROUNDS = 15;
  const [state, setState]           = useState('idle'); // idle|wait|go|done|wrong
  const [current, setCurrent]       = useState(null);
  const [round, setRound]           = useState(0);
  const [correct, setCorrect]       = useState(0);
  const [times, setTimes]           = useState([]);
  const [finished, setFinished]     = useState(false);
  const [best, setBest]             = useStorage('gm_dirflash_best', null);
  const [showInvite, setShowInvite] = useState(false);
  const [partner, setPartner]       = useState(null);
  const [showResults, setShowResults] = useState(null);

  // Auto-set partner when arriving from game room invite
  useEffect(() => {
    if (invitePartner) { setPartner(invitePartner); }
  }, [invitePartner]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRef  = useRef(null);
  const timerRef  = useRef(null);
  const timesRef  = useRef([]);
  const correctRef= useRef(0);

  const nextRound = (rnd) => {
    setState('wait');
    const dir = DIRS[Math.floor(Math.random() * 4)];
    setCurrent(dir);
    timerRef.current = setTimeout(() => {
      setState('go');
      startRef.current = Date.now();
    }, 800 + Math.random() * 1400);
  };

  const start = () => {
    timesRef.current = []; correctRef.current = 0;
    setTimes([]); setCorrect(0); setRound(1);
    setFinished(false); setShowResults(null);
    nextRound(1);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (finished) {
      const avg = timesRef.current.length ? Math.round(timesRef.current.reduce((a, b) => a + b, 0) / timesRef.current.length) : 1500;
      const stored = Math.max(1, 1500 - avg);
      if (!best || stored > best) setBest(stored);
      saveGameScore('directionflash', stored);
      if (partner) setShowResults({ myScore: avg, partnerScore: Math.floor(Math.random() * 150) + 250 });
    }
  }, [finished]); // eslint-disable-line

  const tap = (dirId) => {
    if (state === 'wait') { clearTimeout(timerRef.current); setState('wrong'); setTimeout(() => nextRound(round), 900); return; }
    if (state !== 'go' || !current) return;
    const ms = Date.now() - startRef.current;
    clearTimeout(timerRef.current);

    if (dirId === current.id) {
      correctRef.current++;
      setCorrect(c => c + 1);
      timesRef.current = [...timesRef.current, ms];
      setTimes(t => [...t, ms]);
      setState('done');
    } else {
      setState('wrong');
    }

    const nextR = round + 1;
    if (nextR > ROUNDS) { setTimeout(() => setFinished(true), 600); }
    else { setRound(nextR); setTimeout(() => nextRound(nextR), 700); }
  };

  const avgMs = times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : null;
  const bestDisplay = best ? `${1500 - best}ms` : '–';

  if (showResults) return <MultiplayerResults lang={lang} myScore={showResults.myScore} myLabel="ms avg" partnerId={partner?.id} partnerScore={showResults.partnerScore} partnerLabel="ms avg" aiComment={nl ? `Gemiddelde reactietijd van ${showResults.myScore}ms. Direction Flash traint richtingsreactie en cognitieve snelheid.` : `Average reaction time of ${showResults.myScore}ms. Direction Flash trains directional reaction and cognitive speed.`} onClose={() => { setShowResults(null); setPartner(null); }} onRematch={start} />;

  return (
    <div>
      {showInvite && <MultiplayerInviteModal lang={lang} title="Direction Flash" icon="🧭" allUsers={allUsers} friends={friends} onInvite={(user) => { setPartner(user); setShowInvite(false); }} onClose={() => setShowInvite(false)} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={() => onBack()} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>■ {nl ? 'Stop' : 'Stop'}</button>
        <button onClick={() => setShowInvite(true)} style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, color: C.white, borderRadius: 10, padding: '5px 12px', cursor: 'pointer', fontSize: 11 }}>👥 {nl ? 'Samen' : 'Together'}</button>
      </div>
      {partner && <div style={{ ...s_GC({ padding: '8px 12px', marginBottom: 10 }), display: 'flex', alignItems: 'center', gap: 8 }}><Avatar user={partner} size={26} /><span style={{ fontSize: 12, color: C.teal }}>{partner.name} {nl ? 'speelt mee' : 'is playing'} 🟢</span></div>}

      {state === 'idle' && !finished && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🧭</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{nl ? 'Richting Flash' : 'Direction Flash'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 4, lineHeight: 1.6 }}>
            {nl ? `Een pijl verschijnt. Tik zo snel mogelijk de juiste richting hieronder. ${ROUNDS} rondes.` : `An arrow appears. Tap the matching direction button below as fast as possible. ${ROUNDS} rounds.`}
          </div>
          <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 12 }), display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, textAlign: 'center' }}>
            <div><div style={{ fontSize: 11, color: C.silver }}>BEST</div><div style={{ fontSize: 18, color: C.teal, fontWeight: 700 }}>{bestDisplay}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>{nl ? 'RONDES' : 'ROUNDS'}</div><div style={{ fontSize: 18, color: _T.accent, fontWeight: 700 }}>{ROUNDS}</div></div>
          </div>
          <button style={s_PB({ marginTop: 8 })} onClick={start}>{nl ? 'Starten' : 'Start'}</button>
        </div>
      )}

      {(state === 'wait' || state === 'go' || state === 'done' || state === 'wrong') && !finished && (
        <>
          <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 12 }), display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
            <div><div style={{ fontSize: 11, color: C.silver }}>{nl ? 'RONDE' : 'ROUND'}</div><div style={{ fontSize: 20, color: _T.accent, fontWeight: 700 }}>{round}/{ROUNDS}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>AVG</div><div style={{ fontSize: 20, color: C.teal, fontWeight: 700 }}>{avgMs ? `${avgMs}ms` : '–'}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>✓</div><div style={{ fontSize: 20, color: C.teal, fontWeight: 700 }}>{correct}</div></div>
          </div>

          <div style={{ ...s_GC({ textAlign: 'center', padding: '32px 20px', marginBottom: 16 }), background: state === 'go' ? `${_T.accent}20` : state === 'wrong' ? `${C.red}15` : _T.bg3 }}>
            {state === 'wait' && <><div style={{ fontSize: 48 }}>⏳</div><div style={{ color: C.orange, fontWeight: 700, fontSize: 15, marginTop: 8 }}>{nl ? 'Wacht...' : 'Wait...'}</div></>}
            {state === 'go' && current && <div className="popIn" style={{ fontSize: 100, lineHeight: 1 }}>{current.sym}</div>}
            {state === 'done' && <><div style={{ fontSize: 48 }}>✓</div><div style={{ color: C.teal, fontSize: 18, fontWeight: 700, marginTop: 4 }}>{times[times.length - 1]}ms</div></>}
            {state === 'wrong' && <><div style={{ fontSize: 48 }}>✗</div><div style={{ color: C.red, fontWeight: 700, marginTop: 4 }}>{nl ? 'Te vroeg of verkeerd!' : 'Too early or wrong!'}</div></>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {DIRS.map(d => (
              <button key={d.id} onClick={() => tap(d.id)} style={{ padding: '18px', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, cursor: 'pointer', fontSize: 28, fontWeight: 700, color: C.white, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                {d.sym}
                <span style={{ fontSize: 10, color: C.silver }}>{nl ? d.label : d.labelEN}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {finished && !showResults && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 34, color: _T.accent, fontWeight: 700, marginBottom: 4 }}>{avgMs ? `${avgMs}ms` : '–'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 2 }}>{nl ? 'gemiddelde reactietijd' : 'average reaction time'}</div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 12 }}>✓ {correct}/{ROUNDS}</div>
          <button style={s_PB()} onClick={start}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      )}
    </div>
  );
}

export default DirectionFlashGame;
